import { useEffect, useRef } from "react";
import type { HeroFuelStatus, LandingStats } from "@/lib/landingStats";
import PhoneMockup from "./PhoneMockup";

interface HeroSectionProps {
  theme: "dark" | "light";
  lang: "fr" | "en" | "ar";
  stats: LandingStats;
}

const FALLBACK_DIESEL_PRICE = 50.12;
const FALLBACK_GAS_PRICE = 50.13;

const content = {
  fr: {
    badge: "Disponible en Mauritanie",
    h1a: "Trouvez du carburant",
    h1b: "avant d'être à sec",
    sub: "Disponibilité des stations en temps réel pour toute la Mauritanie — alimenté par la communauté.",
    appStore: "App Store",
    googlePlay: "Google Play",
    statWilayas: "Wilayas",
    statStations: "Stations",
    statFreshness: "Mises à jour",
    fuelDiesel: "Diesel",
    fuelGasoline: "Essence",
    floatDiesel: "Diesel · Disponible",
    floatGasoline: "Essence · Disponible",
    floatNearby: "3 stations à proximité",
  },
  en: {
    badge: "Available in Mauritania",
    h1a: "Find fuel",
    h1b: "before you run out",
    sub: "Real-time station availability across all of Mauritania — powered by the community.",
    appStore: "App Store",
    googlePlay: "Google Play",
    statWilayas: "Wilayas",
    statStations: "Stations",
    statFreshness: "Updates",
    fuelDiesel: "Diesel",
    fuelGasoline: "Gasoline",
    floatDiesel: "Diesel · Available",
    floatGasoline: "Gasoline · Available",
    floatNearby: "3 stations nearby",
  },
  ar: {
    badge: "متاح في موريتانيا",
    h1a: "اعثر على الوقود",
    h1b: "قبل أن تنفد",
    sub: "توفر المحطات في الوقت الفعلي عبر موريتانيا — مدعوم من المجتمع.",
    appStore: "App Store",
    googlePlay: "Google Play",
    statWilayas: "ولاية",
    statStations: "محطة",
    statFreshness: "تحديثات",
    fuelDiesel: "ديزل",
    fuelGasoline: "بنزين",
    floatDiesel: "ديزل · متاح",
    floatGasoline: "بنزين · متاح",
    floatNearby: "3 محطات قريبة",
  },
};

function formatRelativeFreshness(iso: string, lang: "fr" | "en" | "ar"): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "…";
  const sec = Math.round((Date.now() - t) / 1000);
  if (sec < 45) {
    return lang === "fr" ? "À l'instant" : lang === "en" ? "Just now" : "الآن";
  }
  const min = Math.floor(sec / 60);
  if (min < 60) {
    if (lang === "fr") return min <= 1 ? "Il y a 1 min" : `Il y a ${min} min`;
    if (lang === "en") return min <= 1 ? "1m ago" : `${min}m ago`;
    return min <= 1 ? "منذ دقيقة" : `منذ ${min} د`;
  }
  const h = Math.floor(min / 60);
  if (h < 48) {
    if (lang === "fr") return h <= 1 ? "Il y a 1 h" : `Il y a ${h} h`;
    if (lang === "en") return h <= 1 ? "1h ago" : `${h}h ago`;
    return h <= 1 ? "منذ ساعة" : `منذ ${h} س`;
  }
  return lang === "fr" ? "Récemment" : lang === "en" ? "Recently" : "مؤخرًا";
}

function statusLabel(status: HeroFuelStatus, lang: "fr" | "en" | "ar"): string {
  if (status === "available") return lang === "fr" ? "Disponible" : lang === "en" ? "Available" : "متاح";
  if (status === "low") return lang === "fr" ? "Stock faible" : lang === "en" ? "Low stock" : "مخزون منخفض";
  if (status === "out") return lang === "fr" ? "Rupture" : lang === "en" ? "Out of stock" : "نفد";
  return lang === "fr" ? "N/D" : lang === "en" ? "N/A" : "غير محدد";
}

function statusColor(status: HeroFuelStatus): string {
  if (status === "available") return "#34C759";
  if (status === "low") return "#FF9500";
  if (status === "out") return "#FF3B30";
  return "rgba(255,255,255,0.35)";
}

function formatHeroPrice(price: number | null, fallback: number, hasSnapshot: boolean): string {
  if (price != null && Number.isFinite(price)) return `${price.toFixed(2)} MRU/L`;
  if (!hasSnapshot) return `${fallback.toFixed(2)} MRU/L`;
  return "—";
}

function heroNearbyLine(count: number | null, lang: "fr" | "en" | "ar", fallback: string): string {
  if (count == null) return fallback;
  if (lang === "fr") {
    if (count === 0) return "Aucune station dispo à proximité";
    if (count === 1) return "1 station dispo à proximité";
    return `${count} stations dispo à proximité`;
  }
  if (lang === "en") {
    if (count === 0) return "No stations with fuel nearby";
    if (count === 1) return "1 station nearby with fuel";
    return `${count} stations nearby with fuel`;
  }
  if (count === 0) return "لا محطات متوفرة بالقرب";
  if (count === 1) return "محطة واحدة بالقرب";
  return `${count} محطات بالقرب`;
}

export default function HeroSection({ theme, lang, stats }: HeroSectionProps) {
  const isDark = theme === "dark";
  const t = content[lang];
  const isRtl = lang === "ar";
  const snap = stats.heroSnapshot;

  const dieselStatus = snap?.diesel.status ?? null;
  const gasStatus = snap?.gasoline.status ?? null;
  const dieselLabel =
    snap && dieselStatus != null
      ? statusLabel(dieselStatus, lang)
      : (t.floatDiesel.split("·")[1]?.trim() ?? "…");
  const gasLabel =
    snap && gasStatus != null
      ? statusLabel(gasStatus, lang)
      : (t.floatGasoline.split("·")[1]?.trim() ?? "…");
  const dieselPriceLine = formatHeroPrice(snap?.diesel.priceMruPerLiter ?? null, FALLBACK_DIESEL_PRICE, Boolean(snap));
  const gasPriceLine = formatHeroPrice(snap?.gasoline.priceMruPerLiter ?? null, FALLBACK_GAS_PRICE, Boolean(snap));
  const dieselDot = snap && dieselStatus != null ? statusColor(dieselStatus) : "#34C759";
  const gasDot = snap && gasStatus != null ? statusColor(gasStatus) : "#34C759";
  const nearbyText = heroNearbyLine(snap?.nearbyAvailableCount ?? null, lang, t.floatNearby);

  const statRows = [
    { value: String(stats.wilayas), label: t.statWilayas },
    {
      value: lang === "ar" ? `+${stats.stations}` : `${stats.stations}+`,
      label: t.statStations,
    },
    { value: formatRelativeFreshness(stats.updatedAt, lang), label: t.statFreshness },
  ];

  const glassCard = isDark
    ? "backdrop-blur-[20px] border border-white/10"
    : "backdrop-blur-[20px] bg-white/90 border border-black/10";

  const textPrimary = isDark ? "text-white" : "text-gray-900";
  const textMuted = isDark ? "text-white/60" : "text-gray-500";

  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = heroRef.current;
    if (!el) return;
    el.style.opacity = "0";
    el.style.transform = "translateY(20px)";
    requestAnimationFrame(() => {
      el.style.transition = "opacity 0.8s ease, transform 0.8s ease";
      el.style.opacity = "1";
      el.style.transform = "translateY(0)";
    });
  }, []);

  return (
    <section
      className="relative min-h-screen flex items-center pt-16 overflow-hidden"
      style={{ background: isDark ? "#000000" : "#F2F2F7" }}
      dir={isRtl ? "rtl" : "ltr"}
    >
      {/* Background radial glow */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: "radial-gradient(circle at 65% 50%, rgba(0,122,255,0.10) 0%, transparent 60%)",
        }}
      />
      {/* Subtle grid */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.03]"
        style={{
          backgroundImage: `linear-gradient(${isDark ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.5)"} 1px, transparent 1px), linear-gradient(90deg, ${isDark ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.5)"} 1px, transparent 1px)`,
          backgroundSize: "60px 60px",
        }}
      />

      <div ref={heroRef} className="relative z-10 max-w-[1280px] mx-auto px-6 md:px-10 w-full py-20">
        <div className="flex flex-col lg:flex-row items-center gap-16">
          {/* LEFT COLUMN */}
          <div className="flex-1 flex flex-col gap-6 max-w-[520px]">
            {/* Badge */}
            <div className="self-start flex items-center gap-2 px-3 py-1.5 rounded-full" style={{ background: "rgba(52,199,89,0.12)", border: "1px solid rgba(52,199,89,0.25)" }}>
              <div className="w-2 h-2 rounded-full bg-[#34C759] animate-pulse" />
              <span className="text-[#34C759] text-sm font-medium">{t.badge}</span>
            </div>

            {/* H1 */}
            <h1 className={`text-5xl md:text-6xl font-bold leading-[1.1] tracking-tight ${textPrimary}`} style={{ fontFamily: "-apple-system, 'SF Pro Display', system-ui, sans-serif" }}>
              {t.h1a}{" "}
              <span style={{ background: "linear-gradient(135deg, #007AFF 0%, #34C759 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>
                {t.h1b}
              </span>
            </h1>

            {/* Subtitle */}
            <p className={`text-lg leading-relaxed max-w-[440px] ${textMuted}`} style={{ fontFamily: "-apple-system, 'SF Pro Text', system-ui, sans-serif" }}>
              {t.sub}
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-3">
              <a
                href="#download"
                onClick={e => { e.preventDefault(); document.getElementById("download")?.scrollIntoView({ behavior: "smooth" }); }}
                className={`flex items-center gap-3 px-5 py-3 rounded-full cursor-pointer whitespace-nowrap transition-all duration-200 hover:scale-[1.02] active:scale-[0.97] ${isDark ? "bg-black border border-white/15 text-white hover:bg-white/10" : "bg-gray-900 border border-black/10 text-white hover:bg-gray-800"}`}
                style={{ backdropFilter: "blur(20px)" }}
              >
                <i className="ri-apple-fill text-xl" />
                <div className="flex flex-col items-start">
                  <span className="text-[10px] opacity-70 leading-none">Download on the</span>
                  <span className="text-sm font-semibold leading-tight">{t.appStore}</span>
                </div>
              </a>
              <a
                href="#download"
                onClick={e => { e.preventDefault(); document.getElementById("download")?.scrollIntoView({ behavior: "smooth" }); }}
                className={`flex items-center gap-3 px-5 py-3 rounded-full cursor-pointer whitespace-nowrap transition-all duration-200 hover:scale-[1.02] active:scale-[0.97] ${isDark ? "bg-[rgba(28,28,30,0.90)] border border-white/10 text-white hover:bg-white/10" : "bg-white/90 border border-black/10 text-gray-900 hover:bg-white"}`}
                style={{ backdropFilter: "blur(20px)" }}
              >
                <i className="ri-google-play-fill text-xl text-[#34C759]" />
                <div className="flex flex-col items-start">
                  <span className="text-[10px] opacity-70 leading-none">Get it on</span>
                  <span className="text-sm font-semibold leading-tight">{t.googlePlay}</span>
                </div>
              </a>
            </div>

            {/* Stats row — wilayas / stations from API; freshness from updatedAt */}
            <div className="flex items-center gap-6 pt-2">
              {statRows.map((s, i) => (
                <div key={i} className="flex items-center gap-6">
                  <div className="flex flex-col">
                    <span className={`text-xl font-bold ${textPrimary}`} style={{ fontFamily: "-apple-system, 'SF Pro Display', system-ui, sans-serif" }}>{s.value}</span>
                    <span className={`text-xs ${textMuted}`}>{s.label}</span>
                  </div>
                  {i < statRows.length - 1 && (
                    <div className={`h-8 w-px ${isDark ? "bg-white/15" : "bg-black/15"}`} />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT COLUMN — Phone + floating cards */}
          <div className="flex-1 flex items-center justify-center relative" style={{ minHeight: 680 }}>
            {/* Glow behind phone */}
            <div
              className="absolute rounded-full pointer-events-none"
              style={{
                width: 380,
                height: 380,
                background: isDark
                  ? "radial-gradient(circle, rgba(0,122,255,0.18) 0%, transparent 70%)"
                  : "radial-gradient(circle, rgba(0,122,255,0.10) 0%, transparent 70%)",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                filter: "blur(50px)",
              }}
            />

            {/* Phone */}
            <div className="relative z-10">
              <PhoneMockup />
            </div>

            {/* Floating card — left (Diesel) */}
            <div
              className={`absolute hidden lg:flex items-center gap-3 px-4 py-3 rounded-2xl ${glassCard} shadow-xl`}
              style={{
                left: isRtl ? "auto" : -20,
                right: isRtl ? -20 : "auto",
                top: "28%",
                animation: "floatY 3s ease-in-out infinite",
                minWidth: 190,
                background: isDark ? "rgba(28,28,30,0.95)" : undefined,
              }}
            >
              <div className="w-8 h-8 flex items-center justify-center rounded-xl" style={{ background: "rgba(0,122,255,0.15)" }}>
                <span className="text-base">⛽</span>
              </div>
              <div>
                <p className={`text-xs font-semibold ${textPrimary}`}>{t.fuelDiesel}</p>
                <div className="flex items-center gap-1 mt-0.5">
                  <div className="w-1.5 h-1.5 rounded-full" style={{ background: dieselDot }} />
                  <span className="text-[10px] font-medium" style={{ color: dieselDot }}>{dieselLabel}</span>
                </div>
                <p className={`text-[10px] ${textMuted} mt-0.5`}>{dieselPriceLine}</p>
              </div>
            </div>

            {/* Floating card — right (Gasoline) */}
            <div
              className={`absolute hidden lg:flex items-center gap-3 px-4 py-3 rounded-2xl ${glassCard} shadow-xl`}
              style={{
                right: isRtl ? "auto" : -20,
                left: isRtl ? -20 : "auto",
                top: "42%",
                animation: "floatY 2.5s ease-in-out infinite 0.8s",
                minWidth: 190,
                background: isDark ? "rgba(28,28,30,0.95)" : undefined,
              }}
            >
              <div className="w-8 h-8 flex items-center justify-center rounded-xl" style={{ background: "rgba(52,199,89,0.15)" }}>
                <span className="text-base">⛽</span>
              </div>
              <div>
                <p className={`text-xs font-semibold ${textPrimary}`}>{t.fuelGasoline}</p>
                <div className="flex items-center gap-1 mt-0.5">
                  <div className="w-1.5 h-1.5 rounded-full" style={{ background: gasDot }} />
                  <span className="text-[10px] font-medium" style={{ color: gasDot }}>{gasLabel}</span>
                </div>
                <p className={`text-[10px] ${textMuted} mt-0.5`}>{gasPriceLine}</p>
              </div>
            </div>

            {/* Floating card — bottom right (Nearby) */}
            <div
              className={`absolute hidden lg:flex items-center gap-3 px-4 py-3 rounded-2xl ${glassCard} shadow-xl`}
              style={{
                right: isRtl ? "auto" : -10,
                left: isRtl ? -10 : "auto",
                bottom: "12%",
                animation: "floatY 4s ease-in-out infinite 1.5s",
                minWidth: 170,
                background: isDark ? "rgba(28,28,30,0.95)" : undefined,
              }}
            >
              <div className="w-8 h-8 flex items-center justify-center rounded-xl" style={{ background: "rgba(255,59,48,0.12)" }}>
                <i className="ri-map-pin-line text-[#FF3B30] text-sm" />
              </div>
              <p className={`text-xs font-semibold ${textPrimary}`}>{nearbyText}</p>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes floatY {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
      `}</style>
    </section>
  );
}
